import { useState, useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import Custom404 from "../404";

import Image from "next/image";
import Link from "next/link";
import router from "next/router";

import {
  Grid,
  Typography,
  Stack,
  Rating,
  Chip,
  Tab,
  useMediaQuery,
  Box,
} from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab/";
import { styled, useTheme } from "@mui/material/styles";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";

import { wrapper } from "../../redux/store";
import {
  getBookBySlug,
  getRandomRecommendedBooksByCategory,
} from "../../redux/actions/books.action";
import { addBookToCart } from "../../redux/actions/cart.action";

import Breadcrumb from "../../components/Common/Breadcrumb.component";
import ContentBox from "../../components/Common/ContentBox.component";
import CustomButton from "../../components/Common/CustomButton.component";
import CustomCard from "../../components/Common/CustomCard.component";
import Reviews from "../../components/Common/Reviews.component";
import CardSkeleton from "../../components/Common/CardSkeleton.component";

import { skeletonArray } from "../../services/utils";

const RatingContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),

  "& .MuiTypography-caption": {
    marginLeft: theme.spacing(1),
  },
}));

const CategoryLinkContainer = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: "bold",
  marginBottom: theme.spacing(2),
  width: "fit-content",

  "&:hover": {
    textShadow: theme.customShadows.textShadow1,
    ...theme.animations.scaleText,
  },
}));

const Book = (props) => {
  const [tabValue, setTabValue] = useState("description");
  const [successSound, setSuccessSound] = useState(null);
  const [failSound, setFailSound] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  const { book, booksByCategory, error } = useSelector((state) => state.books);
  const { newReviewID } = useSelector((state) => state.reviews);

  const theme = useTheme();
  const matchesMD = useMediaQuery(theme.breakpoints.down("lg"));

  const listBreadcrumbChildren = useMemo(() => {
    return [
      {
        name: "Thể loại",
        href: "/categories",
      },
      {
        name: book?.category?.title,
        href: `/categories/${book?.category?.slug}`,
      },
      {
        name: book?.title,
      },
    ];
  }, [book?.title, book?.category]);

  useEffect(() => {
    if (book) {
      setIsLoading(true);
      const queryParams = {
        category: book?.category?._id,
        book: book?._id,
      };
      dispatch(getRandomRecommendedBooksByCategory(queryParams));
    }
  }, [book, dispatch]);

  useEffect(() => {
    setSuccessSound(new Audio("/static/sounds/successSound.mp3"));
    setFailSound(new Audio("/static/sounds/failSound.mp3"));
  }, []);

  useEffect(() => {
    if (newReviewID) {
      dispatch(getBookBySlug(router.query.slug));
    }
  }, [newReviewID, dispatch]);

  useEffect(() => {
    if (booksByCategory?.books.length) {
      setIsLoading(false);
    }
  }, [booksByCategory]);

  useEffect(() => {
    setTabValue("description");
  }, [book]);

  const playSound = (type) => {
    type === "success" ? successSound.play() : failSound.play();
  };

  const handleClickAddToCart = (event, book) => {
    event.preventDefault();
    dispatch(addBookToCart(book, playSound));
  };

  const handleChangeTab = (_e, newValue) => {
    setTabValue(newValue);
  };

  return error ? (
    <Custom404 />
  ) : (
    <div>
      <Breadcrumb listBreadcrumbChildren={listBreadcrumbChildren} />
      <Grid container spacing={4}>
        <Grid item xs={12} sm={12} md={11} sx={{ margin: "0 auto" }}>
          <ContentBox>
            <Grid container spacing={{ xs: 9, sm: 10 }} px={4} py={2}>
              <Grid item xs={12} sm={12} md={5}>
                <Box
                  sx={{
                    borderRadius: theme.shape.borderRadius * 2,
                    overflow: "hidden",
                  }}
                >
                  <Image
                    layout="responsive"
                    height="264"
                    width="372"
                    src={`${
                      book?.imageCover
                        ? book?.imageCover
                        : "/static/images/defaultBookCover.svg"
                    }`}
                    alt={book?.slug}
                    objectFit="cover"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={12} md={7}>
                <Stack
                  justifyContent="space-between"
                  spacing={10}
                  sx={{ height: "100%" }}
                >
                  <Stack justifyContent="space-between" spacing={2}>
                    <Typography variant="h5" fontWeight="bold">
                      {book?.title}
                    </Typography>
                    <Stack spacing={4}>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        alignItems={{ xs: "flex-start", sm: "center" }}
                        justifyContent="space-between"
                        sx={{ flexWrap: "wrap" }}
                      >
                        <RatingContainer>
                          <Typography
                            variant="h6"
                            color="#faaf00"
                            fontWeight="bold"
                            fontSize={28}
                          >
                            {`${
                              book?.ratingAverage
                                ? book?.ratingAverage?.toFixed(1)
                                : ""
                            }`}
                          </Typography>
                          <Rating
                            value={book?.ratingAverage || 0}
                            size="large"
                            precision={0.5}
                            readOnly
                            sx={{ marginBottom: theme.spacing(1) }}
                          />
                          <Typography variant="subtitle2">
                            {`(${
                              book?.ratingQuantity ? book?.ratingQuantity : 0
                            } đánh giá)`}
                          </Typography>
                        </RatingContainer>
                        {book?.isRecommended && (
                          <Chip
                            label="Khuyên đọc"
                            color="secondary"
                            size="large"
                            icon={<ThumbUpAltOutlinedIcon />}
                            sx={{
                              color: "inherit",
                            }}
                          />
                        )}
                      </Stack>
                      <Typography>
                        Được mượn{" "}
                        <Typography
                          variant="h6"
                          component="span"
                          color={theme.palette.primary.main}
                          fontSize={theme.typography.fontSize * 1.5}
                          fontWeight="bold"
                        >
                          {book?.borrowQuantity}
                        </Typography>{" "}
                        lần
                      </Typography>
                      <CategoryLinkContainer variant="subtitle1" noWrap>
                        <Link
                          href={`/categories/${book?.category?.slug}`}
                          passHref
                        >
                          {book?.category?.title || ""}
                        </Link>
                      </CategoryLinkContainer>
                    </Stack>
                  </Stack>
                  <Stack>
                    <CustomButton
                      variant="contained"
                      size="large"
                      onClick={(event) => handleClickAddToCart(event, book)}
                      disabled={book?.owners[0]?.quantity < 1}
                    >
                      {book?.owners[0]?.quantity < 1 ? null : (
                        <AddShoppingCartIcon
                          sx={{ marginRight: theme.spacing(1) }}
                        />
                      )}{" "}
                      <Typography variant="body1">
                        {book?.owners[0]?.quantity < 1
                          ? "Đã cho mượn"
                          : "Cho vào giỏ"}
                      </Typography>
                    </CustomButton>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </ContentBox>
        </Grid>
      </Grid>
      <ContentBox title="Sách hay cùng thể loại">
        <Grid container spacing={5}>
          {isLoading
            ? skeletonArray.slice(0, 4).map((_, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <CardSkeleton />
                </Grid>
              ))
            : booksByCategory?.books?.map((book) => (
                <Grid item xs={12} sm={6} md={3} key={book._id}>
                  <CustomCard info={book} haveCategoryLink infoType="books" />
                </Grid>
              ))}
        </Grid>
      </ContentBox>
      <ContentBox title="Chi tiết">
        <TabContext value={tabValue}>
          <TabList
            onChange={handleChangeTab}
            aria-label="Book description tabs"
          >
            <Tab label="Giới thiệu" value="description" />
            <Tab
              label={`Đánh giá & nhận xét (${book?.reviews?.length})`}
              value="review"
            />
          </TabList>
          <TabPanel
            value="description"
            sx={{ padding: !matchesMD ? "3% 10%" : "30px" }}
          >
            <Typography
              style={{
                whiteSpace: "pre-line",
                fontSize: theme.typography.fontSize * 1.3,
                columnCount: matchesMD ? 1 : 2,
                columnRule: `1px solid #ddd`,
                columnGap: theme.spacing(10),
              }}
            >
              {book?.description || "Chúng tôi đang cập nhật thông tin này."}
            </Typography>
          </TabPanel>
          <TabPanel value="review">
            <Reviews book={book} token={props?.token} />
          </TabPanel>
        </TabContext>
      </ContentBox>
    </div>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ query, req }) => {
      await store.dispatch(getBookBySlug(query.slug));

      const token =
        typeof req.cookies["camlambooks_token"] !== "undefined" &&
        JSON.parse(req.cookies["camlambooks_token"]);

      return {
        props: {
          token,
        },
      };
    }
);

export default Book;
